import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PricingHubHeader from './components/PricingHubHeader';
import CalculatorTab from './tabs/CalculatorTab';
import MarginAnalysisTab from './tabs/MarginAnalysisTab';
import StrategyTab from './tabs/StrategyTab';

type TabId = 'calculator' | 'margin' | 'strategy';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
  component: React.FC;
}

const TABS: Tab[] = [
  { id: 'calculator', label: 'Price Calculator', icon: '🧮', component: CalculatorTab },
  { id: 'margin', label: 'Margin Analysis', icon: '📊', component: MarginAnalysisTab },
  { id: 'strategy', label: 'AI Strategy', icon: '💡', component: StrategyTab }
];

const PricingHub: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState<TabId>('calculator');

  // Set document title
  useEffect(() => {
    document.title = 'Pricing Hub | Explanatum';
  }, []);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Handle hash-based navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '') as TabId;
    if (hash && TABS.some(t => t.id === hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Update URL hash when tab changes
  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const ActiveTabComponent = TABS.find(t => t.id === activeTab)?.component || CalculatorTab;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <PricingHubHeader />

        {/* Tab Navigation */}
        <div className="mt-8 border-b border-gray-200 bg-white rounded-t-xl">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors
                  ${activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
              >
                <span className="text-lg">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="rounded-b-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <ActiveTabComponent />
        </div>
      </div>
    </div>
  );
};

export default PricingHub;
